import { type RefObject, useEffect } from 'react';

import { monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';

const SCROLL_ZONE_PX = 80;
const MAX_SCROLL_PX_PER_FRAME_X = 25;
const MAX_SCROLL_PX_PER_FRAME_Y = 15;

/**
 * RAF auto-scroll on all four edges of the timeline container while a Pragmatic
 * drag is in flight. Registers its own `monitorForElements` subscription.
 */
export function useTimelineDragAutoScroll(
  scrollContainerRef: RefObject<HTMLDivElement | null>
): void {
  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;

    let rafId: number | null = null;
    let clientX = 0;
    let clientY = 0;

    function scrollStep() {
      const rect = el!.getBoundingClientRect();
      const fromLeft = clientX - rect.left;
      const fromRight = rect.right - clientX;
      const fromTop = clientY - rect.top;
      const fromBottom = rect.bottom - clientY;

      // Only scroll while cursor is inside this container — otherwise grabbing
      // a TaskList card produces a runaway ramp factor.
      const inHorizontalBounds = fromLeft >= 0 && fromRight >= 0;
      const inVerticalBounds = fromTop >= 0 && fromBottom >= 0;
      if (!inHorizontalBounds || !inVerticalBounds) {
        rafId = requestAnimationFrame(scrollStep);
        return;
      }

      let dx = 0;
      let dy = 0;
      if (fromLeft < SCROLL_ZONE_PX) {
        dx = -MAX_SCROLL_PX_PER_FRAME_X * (1 - fromLeft / SCROLL_ZONE_PX);
      } else if (fromRight < SCROLL_ZONE_PX) {
        dx = MAX_SCROLL_PX_PER_FRAME_X * (1 - fromRight / SCROLL_ZONE_PX);
      }
      if (fromTop < SCROLL_ZONE_PX) {
        dy = -MAX_SCROLL_PX_PER_FRAME_Y * (1 - fromTop / SCROLL_ZONE_PX);
      } else if (fromBottom < SCROLL_ZONE_PX) {
        dy = MAX_SCROLL_PX_PER_FRAME_Y * (1 - fromBottom / SCROLL_ZONE_PX);
      }

      if (dx !== 0 || dy !== 0) el!.scrollBy({ left: dx, top: dy });
      rafId = requestAnimationFrame(scrollStep);
    }

    function stopScroll() {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
    }

    const cleanupMonitor = monitorForElements({
      onDragStart: ({ location }) => {
        clientX = location.current.input.clientX;
        clientY = location.current.input.clientY;
        rafId = requestAnimationFrame(scrollStep);
      },
      onDrag: ({ location }) => {
        clientX = location.current.input.clientX;
        clientY = location.current.input.clientY;
      },
      onDrop: stopScroll
    });

    return () => {
      stopScroll();
      cleanupMonitor();
    };
  }, [scrollContainerRef]);
}
